// Pure SIP-invite leg classification, extracted from use-softphone so it can be unit-tested without a SIP
// session. The softphone owns a SINGLE live session and treats two kinds of inbound INVITE differently:
//
//   - Internal dialer leg: carries X-Sales-Call-Id and NO X-Sales-Inbound. The backend originated it for
//     this agent (outbound dialer leg, or a PARKED hot-transfer booker leg). It is AUTO-ANSWERED and never
//     rings or becomes an inbound customer call on its own.
//   - Inbound customer call: carries X-Sales-Inbound (set by the DID bridge), or has no X-Sales-Call-Id at
//     all (anonymous). It RINGS for the agent to answer.
//
// A hot-transfer parked booker leg is, by this contract, just an internal dialer leg (X-Sales-Call-Id set,
// X-Sales-Inbound absent) — so it auto-answers via exactly the same path; the X-Hot-Transfer-Id header is
// not part of this pure classifier, but the softphone UI uses it to keep the parked leg in the background
// until the customer is actually bridged to the booker.
export function classifyInviteLeg(params: { salesCallId: string | null; hasInboundHeader: boolean }): {
  isInboundCustomerCall: boolean;
  isInternalDialerLeg: boolean;
} {
  const isInboundCustomerCall = params.hasInboundHeader || !params.salesCallId;
  return { isInboundCustomerCall, isInternalDialerLeg: !isInboundCustomerCall };
}

// Whether an incoming INVITE must be rejected (486 Busy) because the softphone
// already owns a call. The softphone owns a SINGLE live session; an inbound
// invite must never replace it.
//
// Internal dialer legs are calls this agent's own dialer just requested. Without
// a live SIP session no media is flowing, so a tracked call or phase that still
// reads "busy" is stale client state (SSE/poll lag — the previous call already
// ended server-side). Rejecting the leg with 486 strands the auto-dialer on the
// previous contact; only a real session or a pending inbound-call decision
// blocks a dialer leg.
export function isInviteBusy(params: {
  isInternalDialerLeg: boolean;
  inviteMatchesCurrentCall: boolean;
  hasExistingSession: boolean;
  hasPendingInvite: boolean;
  trackedCallActive: boolean;
  phase: string;
}): boolean {
  if (params.inviteMatchesCurrentCall) {
    return false;
  }
  if (params.isInternalDialerLeg) {
    return params.hasExistingSession || params.phase === 'ringing' || params.hasPendingInvite;
  }
  return (
    params.hasExistingSession
    || params.trackedCallActive
    || params.phase === 'ringing'
    || params.phase === 'in_call'
    || params.phase === 'calling'
  );
}

// Why an auto-answer that is already in flight must be abandoned instead of
// completed.
//
//   - session_terminated: the leg died while accept() was running — the agent
//     hung up, or the caller/bridge sent CANCEL/BYE.
//   - session_replaced: a newer invite took the single session slot.
//   - runtime_unavailable: this tab's runtime was stopped out from under the
//     answer.
//
// Deliberately NOT a reason: a lame-duck runtime (lease lost, muted, binding
// kept warm for the winning tab). The invite gate refuses to START an answer on
// a lame duck, but one already in flight becomes a live call, and make-before-
// break promises to keep a live call until the winner reports online or the
// grace cap fires — a handoff that is then reclaimed would otherwise have had a
// connected prospect BYE'd out from under it. finishLeaseHandoff's stopRuntime
// disposes the session on schedule, and sets the stopped flag this does read.
export type AutoAnswerAbortReason = 'session_terminated' | 'session_replaced' | 'runtime_unavailable';

// accept() spans microphone acquisition plus SDP/ICE negotiation — seconds on a
// cold headset — and sip.js offers no way to cancel it once started. So the
// answer is validated on both sides of that await instead: the leg has to still
// be THIS runtime's live session when the 200 OK lands, or the answer belongs to
// a call that no longer exists and the leg must be torn down rather than
// reported as connected.
//
// Terminal state is checked FIRST, and the auto-answer failure path depends on
// that order: a failing accept() terminates the invitation itself, and sip.js
// then auto-disposes it, which also clears the hook's session slot. Ranked any
// other way, a mic that would not open would classify as session_replaced,
// which that path treats as an unambiguous abort — and a real device fault
// waved through as benign is how the burn loop comes back.
//
// sessionState values mirror sip.js's SessionState enum ('Initial' |
// 'Establishing' | 'Established' | 'Terminating' | 'Terminated'), compared as
// literals because this module stays free of the SIP runtime; grep for these on
// a sip.js version bump.
export function autoAnswerAbortReason(params: {
  sessionState: string | null | undefined;
  isRuntimeSession: boolean;
  runtimeStopped: boolean;
}): AutoAnswerAbortReason | null {
  if (params.sessionState === 'Terminated' || params.sessionState === 'Terminating') {
    return 'session_terminated';
  }
  if (!params.isRuntimeSession) {
    return 'session_replaced';
  }
  if (params.runtimeStopped) {
    return 'runtime_unavailable';
  }
  return null;
}
